import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import mixpanel from "mixpanel-browser";
import moment from "moment";
import { useQueryClient } from "@tanstack/react-query";
import ContentContainer from "../../templates/content-container/contentContainer";
import ContentContainerToolbar from "../../ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import ReloadButton from "../../ui-components/reload-button/reloadButton";
import { useSelector } from "react-redux";
import { userSelector } from "../../slices/user/userSlice";
import { PAGE_IDS } from "../../app/data/common/pageIds";
import { QUERY_KEYS } from "../../app/data/common/queryKeys";
import XGSErrorMessage from "../../ui-components/error-message/errorMessage";
import {
  OutboundPlanningGridRow,
  OutboundPlanningMetrics,
  useOutboundPlanningGridData,
} from "./useOutboundPlanningGridData";
import TerminalCard from "../../ui-components/terminal-card/terminalCard";
import {
  DEFAULT_FREIGHT_INCLUDES,
  DEFAULT_SORT,
  FreightGroupKey,
  HubDefinition,
  MetricColumn,
  SortKey,
  SortState,
  SortableMetricGroup,
  buildGroupedSections,
  computeAdjustedTotals,
  computeHubSectionTotals,
  computeIncludedPickupsTotal,
  createHubId,
  getPickupsAggregateState,
  GRID_COLUMN_COUNT,
  loadHubsFromStorage,
  saveHubsToStorage,
  sortKeysEqual,
  upsertHubAssignments,
} from "./outboundPlanningGridUtils";
import { ReactComponent as ArrowUpIcon } from "../../images/arrow_up.svg";
import { ReactComponent as ArrowDownIcon } from "../../images/arrow_down.svg";
import "./linehaulOutboundPlanning.scss";

const OUTBOUND_PLANNING_AUTO_REFRESH_MS = 300_000;
const OUTBOUND_PLANNING_RELATIVE_TIME_TICK_MS = 30_000;

function MetricCells({ m, dimmed }: { m: OutboundPlanningMetrics; dimmed?: boolean }) {
  return (
    <>
      <td className={dimmed ? "linehaul-outbound-planning__cell--dimmed" : undefined}>{m.shipments.toLocaleString()}</td>
      <td className={dimmed ? "linehaul-outbound-planning__cell--dimmed" : undefined}>{m.pieces.toLocaleString()}</td>
      <td className={dimmed ? "linehaul-outbound-planning__cell--dimmed" : undefined}>{m.weight.toLocaleString()}</td>
    </>
  );
}

function GroupHeaderWithCheckbox(props: {
  label: string;
  checked: boolean;
  indeterminate?: boolean;
  onChange: (next: boolean) => void;
}) {
  const { label, checked, indeterminate, onChange } = props;
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = !!indeterminate;
  }, [indeterminate]);

  return (
    <div className="linehaul-outbound-planning__group-header">
      <input
        ref={inputRef}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={`Include ${label} in totals`}
      />
      <span>{label}</span>
    </div>
  );
}

function SortIcons(props: { isSorted: false | "asc" | "desc" }) {
  const { isSorted } = props;
  const showUp = isSorted === "asc" || !isSorted;
  const showDown = isSorted === "desc" || !isSorted;
  return (
    <span className="linehaul-outbound-planning__sort-icons">
      {showUp && (
        <span className="linehaul-outbound-planning__sort-icon">
          <ArrowUpIcon />
        </span>
      )}
      {showDown && (
        <span className="linehaul-outbound-planning__sort-icon">
          <ArrowDownIcon />
        </span>
      )}
    </span>
  );
}

function SortableHeader(props: {
  label: React.ReactNode;
  sortKey: SortKey;
  sort: SortState;
  onSort: (key: SortKey) => void;
  align?: "left" | "right";
}) {
  const { label, sortKey, sort, onSort, align = "right" } = props;
  const isActive = sortKeysEqual(sort.key, sortKey);
  const isSorted: false | "asc" | "desc" = isActive ? sort.direction : false;
  return (
    <button
      type="button"
      className={`linehaul-outbound-planning__sortable linehaul-outbound-planning__sortable--${align}`}
      onClick={() => onSort(sortKey)}
    >
      <span className="linehaul-outbound-planning__sortable-label">{label}</span>
      <SortIcons isSorted={isSorted} />
    </button>
  );
}

function MetricHeaderTrio(props: {
  group: SortableMetricGroup;
  sort: SortState;
  onSort: (key: SortKey) => void;
}) {
  const { group, sort, onSort } = props;
  const make = (metric: MetricColumn, label: string) => (
    <th>
      <SortableHeader label={label} sortKey={{ kind: "metric", group, metric }} sort={sort} onSort={onSort} />
    </th>
  );
  return (
    <>
      {make("shipments", "Shipments")}
      {make("pieces", "Pieces")}
      {make("weight", "Weight")}
    </>
  );
}

function PlanningRow(props: {
  row: OutboundPlanningGridRow;
  adjustedTotals: OutboundPlanningMetrics;
  included: Record<FreightGroupKey, boolean>;
}) {
  const { row, adjustedTotals, included } = props;

  return (
    <tr>
      <td className="linehaul-outbound-planning__sticky linehaul-outbound-planning__sticky--1">
        <TerminalCard
          terminalNumber={row.destinationTerminalNumber}
          terminalName={row.terminalName}
          terminalAirportCode={row.airportCode}
        />
      </td>
      <td className="linehaul-outbound-planning__sticky linehaul-outbound-planning__sticky--2">
        {adjustedTotals.shipments.toLocaleString()}
      </td>
      <td className="linehaul-outbound-planning__sticky linehaul-outbound-planning__sticky--3">
        {adjustedTotals.pieces.toLocaleString()}
      </td>
      <td className="linehaul-outbound-planning__sticky linehaul-outbound-planning__sticky--4">
        {adjustedTotals.weight.toLocaleString()}
      </td>
      <MetricCells m={row.inHouse} dimmed={!included.inHouse} />
      <MetricCells m={row.inbound} dimmed={!included.inbound} />
      <MetricCells
        m={computeIncludedPickupsTotal(row, included)}
        dimmed={
          !included.pickupsUnassigned && !included.pickupsAssigned && !included.pickupsCompleted
        }
      />
      <MetricCells m={row.pickupsUnassigned} dimmed={!included.pickupsUnassigned} />
      <MetricCells m={row.pickupsAssigned} dimmed={!included.pickupsAssigned} />
      <MetricCells m={row.pickupsCompleted} dimmed={!included.pickupsCompleted} />
    </tr>
  );
}

export const LinehaulOutboundPlanning: React.FC = () => {
  const userState = useSelector(userSelector);
  const originId = userState.activeTerminal?.id;
  const queryClient = useQueryClient();
  const { rows, isLoading, isFetching, error, enabled } = useOutboundPlanningGridData();

  const [lastRefreshedAt, setLastRefreshedAt] = useState<number | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [, setRelativeTimeTick] = useState(0);
  const didSetInitialRefreshRef = useRef(false);

  const refreshOutboundPlanning = useCallback(async () => {
    if (!enabled) return;
    setIsRefreshing(true);
    try {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.OUTBOUND_PLANNING] });
      setLastRefreshedAt(Date.now());
    } finally {
      setIsRefreshing(false);
    }
  }, [enabled, queryClient]);

  useEffect(() => {
    didSetInitialRefreshRef.current = false;
    setLastRefreshedAt(null);
  }, [originId]);

  useEffect(() => {
    if (!enabled || error) return;
    if (!isLoading && !isFetching && !didSetInitialRefreshRef.current) {
      didSetInitialRefreshRef.current = true;
      setLastRefreshedAt(Date.now());
    }
  }, [enabled, error, isLoading, isFetching]);

  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(() => {
      void refreshOutboundPlanning();
    }, OUTBOUND_PLANNING_AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [enabled, refreshOutboundPlanning]);

  useEffect(() => {
    if (lastRefreshedAt == null) return;
    const id = window.setInterval(() => {
      setRelativeTimeTick((n) => n + 1);
    }, OUTBOUND_PLANNING_RELATIVE_TIME_TICK_MS);
    return () => clearInterval(id);
  }, [lastRefreshedAt]);

  const lastRefreshedLabel =
    lastRefreshedAt == null ? null : moment(lastRefreshedAt).fromNow();

  const [freightIncludes, setFreightIncludes] =
    useState<Record<FreightGroupKey, boolean>>(DEFAULT_FREIGHT_INCLUDES);

  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);

  const handleSort = useCallback((key: SortKey) => {
    setSort((prev) => {
      if (sortKeysEqual(prev.key, key)) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      const initialDir = key.kind === "destination" ? "asc" : "desc";
      return { key, direction: initialDir };
    });
  }, []);

  const [hubsState, setHubsState] = useState<{ originId: number | null; hubs: HubDefinition[] }>({
    originId: null,
    hubs: [],
  });
  const hubs = hubsState.hubs;
  const [hubPanelOpen, setHubPanelOpen] = useState(false);
  const [newHubName, setNewHubName] = useState("");
  const [selectedForNewHub, setSelectedForNewHub] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (typeof originId !== "number") {
      setHubsState({ originId: null, hubs: [] });
      return;
    }
    setHubsState({ originId, hubs: loadHubsFromStorage(originId) });
    setSelectedForNewHub(new Set());
    setNewHubName("");
  }, [originId]);

  useEffect(() => {
    if (hubsState.originId === null || hubsState.originId !== originId) return;
    saveHubsToStorage(hubsState.originId, hubsState.hubs);
  }, [hubsState, originId]);

  const updateHubs = useCallback(
    (updater: (prev: HubDefinition[]) => HubDefinition[]) => {
      setHubsState((prev) => {
        if (prev.originId === null || prev.originId !== originId) return prev;
        return { originId: prev.originId, hubs: updater(prev.hubs) };
      });
    },
    [originId]
  );

  const setInclude = useCallback((key: FreightGroupKey, value: boolean) => {
    setFreightIncludes((prev) => ({ ...prev, [key]: value }));
  }, []);

  /** "Pickups" header is a select-all/deselect-all UI for the 3 sub-types. */
  const setAllPickups = useCallback((value: boolean) => {
    setFreightIncludes((prev) => ({
      ...prev,
      pickupsUnassigned: value,
      pickupsAssigned: value,
      pickupsCompleted: value,
    }));
  }, []);

  const pickupsAggregate = useMemo(
    () => getPickupsAggregateState(freightIncludes),
    [freightIncludes]
  );

  const sections = useMemo(
    () => buildGroupedSections(rows, hubs, sort, freightIncludes),
    [rows, hubs, sort, freightIncludes]
  );

  const showSectionHeaders =
    sections.length > 1 || sections.some((s) => s.hubId !== null && s.rows.length > 0);

  const toggleTerminalForNewHub = useCallback((terminalNumber: number) => {
    setSelectedForNewHub((prev) => {
      const next = new Set(prev);
      if (next.has(terminalNumber)) next.delete(terminalNumber);
      else next.add(terminalNumber);
      return next;
    });
  }, []);

  const addHub = useCallback(() => {
    const name = newHubName.trim();
    if (!name || selectedForNewHub.size === 0) return;
    const id = createHubId();
    const fresh: HubDefinition = { id, name, terminalNumbers: [] };
    updateHubs((prev) => upsertHubAssignments([...prev, fresh], id, [...selectedForNewHub]));
    setNewHubName("");
    setSelectedForNewHub(new Set());
  }, [newHubName, selectedForNewHub, updateHubs]);

  const removeHub = useCallback(
    (hubId: string) => {
      updateHubs((prev) => prev.filter((h) => h.id !== hubId));
    },
    [updateHubs]
  );

  useEffect(() => {
    mixpanel.track("page_viewed", { page: "linehaul_outbound_planning" });
  }, [userState.activeTerminal?.id]);

  const terminalChoices = useMemo(() => {
    return [...rows].sort((a, b) => a.terminalName.localeCompare(b.terminalName));
  }, [rows]);

  return (
    <ContentContainer
      mods={{ "full-width": true }}
      titleComponent={
        <ContentContainerToolbar
          title="Outbound Planning"
          pageId={PAGE_IDS.LINEHAUL_OUTBOUND_PLANNING}
          isLoading={isLoading}
          mods={{ "full-width": true }}
        >
          <div className="linehaul-outbound-planning__refresh-toolbar">
            <span className="linehaul-outbound-planning__last-refreshed">
              Last refreshed {lastRefreshedLabel ?? "—"}
            </span>
            <ReloadButton
              title="Refresh"
              spinner={isRefreshing}
              disabled={isRefreshing || !enabled}
              onClick={() => {
                void refreshOutboundPlanning();
              }}
            />
          </div>
        </ContentContainerToolbar>
      }
    >
      {!enabled && (
        <p className="linehaul-outbound-planning__hint">Select an active terminal to load outbound planning data.</p>
      )}
      {enabled && error && <XGSErrorMessage>Error loading outbound planning data. Please try again.</XGSErrorMessage>}
      {enabled && !error && rows.length === 0 && !isLoading && (
        <div className="linehaul-outbound-planning__empty">No outbound freight found for this terminal.</div>
      )}
      {enabled && !error && rows.length > 0 && (
        <>
          <div className="linehaul-outbound-planning__toolbar">
            <button
              type="button"
              className="linehaul-outbound-planning__hub-toggle"
              onClick={() => setHubPanelOpen((v) => !v)}
            >
              {hubPanelOpen ? "Hide hub groups" : "Destination hub groups"}
              {hubs.length > 0 ? ` (${hubs.length})` : ""}
            </button>
          </div>

          {hubPanelOpen && (
            <div className="linehaul-outbound-planning__hub-panel">
              <p className="linehaul-outbound-planning__hub-help">
                Group destinations that share a linehaul hub (hub-and-spoke). Terminals can only belong to one hub;
                assigning moves them from another hub.
              </p>
              {hubs.map((hub) => (
                <div key={hub.id} className="linehaul-outbound-planning__hub-card">
                  <div className="linehaul-outbound-planning__hub-card-head">
                    <strong>{hub.name}</strong>
                    <button type="button" className="linehaul-outbound-planning__link-btn" onClick={() => removeHub(hub.id)}>
                      Remove hub
                    </button>
                  </div>
                  <div className="linehaul-outbound-planning__hub-terminals">
                    {hub.terminalNumbers.length === 0
                      ? "No terminals assigned."
                      : hub.terminalNumbers
                          .slice()
                          .sort((a, b) => a - b)
                          .join(", ")}
                  </div>
                </div>
              ))}

              <div className="linehaul-outbound-planning__hub-add">
                <div className="linehaul-outbound-planning__hub-add-row">
                  <input
                    type="text"
                    className="linehaul-outbound-planning__hub-name-input"
                    placeholder="New hub name"
                    value={newHubName}
                    onChange={(e) => setNewHubName(e.target.value)}
                  />
                  <button
                    type="button"
                    className="linehaul-outbound-planning__btn-primary"
                    onClick={addHub}
                    disabled={!newHubName.trim() || selectedForNewHub.size === 0}
                  >
                    Create hub
                  </button>
                </div>
                <div className="linehaul-outbound-planning__hub-checkboxes">
                  {terminalChoices.map((r) => (
                    <label key={r.destinationTerminalNumber} className="linehaul-outbound-planning__hub-check-label">
                      <input
                        type="checkbox"
                        checked={selectedForNewHub.has(r.destinationTerminalNumber)}
                        onChange={() => toggleTerminalForNewHub(r.destinationTerminalNumber)}
                      />
                      <span className="linehaul-outbound-planning__hub-check-terminal">
                        <TerminalCard
                          terminalNumber={r.destinationTerminalNumber}
                          terminalName={r.terminalName}
                          terminalAirportCode={r.airportCode}
                          showHoverCard={false}
                        />
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="linehaul-outbound-planning__scroll">
            <table className="linehaul-outbound-planning__table" role="grid">
              <colgroup>
                <col className="linehaul-outbound-planning__col--destination" />
                <col />
                <col />
                <col />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
                <col className="linehaul-outbound-planning__col--metric" />
              </colgroup>
              <thead>
                <tr>
                  <th rowSpan={2} className="linehaul-outbound-planning__sticky linehaul-outbound-planning__sticky--1">
                    <SortableHeader
                      label="Destination terminal"
                      sortKey={{ kind: "destination" }}
                      sort={sort}
                      onSort={handleSort}
                      align="left"
                    />
                  </th>
                  <th colSpan={3} className="linehaul-outbound-planning__sticky linehaul-outbound-planning__sticky--totals">
                    Totals
                  </th>
                  <th colSpan={3}>
                    <GroupHeaderWithCheckbox
                      label="In-house freight"
                      checked={freightIncludes.inHouse}
                      onChange={(v) => setInclude("inHouse", v)}
                    />
                  </th>
                  <th colSpan={3}>
                    <GroupHeaderWithCheckbox
                      label="Inbound freight"
                      checked={freightIncludes.inbound}
                      onChange={(v) => setInclude("inbound", v)}
                    />
                  </th>
                  <th colSpan={3}>
                    <GroupHeaderWithCheckbox
                      label="Pickups"
                      checked={pickupsAggregate.allChecked}
                      indeterminate={pickupsAggregate.indeterminate}
                      onChange={(v) => setAllPickups(v)}
                    />
                  </th>
                  <th colSpan={3}>
                    <GroupHeaderWithCheckbox
                      label="Pickups — Unassigned"
                      checked={freightIncludes.pickupsUnassigned}
                      onChange={(v) => setInclude("pickupsUnassigned", v)}
                    />
                  </th>
                  <th colSpan={3}>
                    <GroupHeaderWithCheckbox
                      label="Pickups — Assigned"
                      checked={freightIncludes.pickupsAssigned}
                      onChange={(v) => setInclude("pickupsAssigned", v)}
                    />
                  </th>
                  <th colSpan={3}>
                    <GroupHeaderWithCheckbox
                      label="Pickups — Completed"
                      checked={freightIncludes.pickupsCompleted}
                      onChange={(v) => setInclude("pickupsCompleted", v)}
                    />
                  </th>
                </tr>
                <tr>
                  <th className="linehaul-outbound-planning__sticky linehaul-outbound-planning__sticky--2">
                    <SortableHeader
                      label="Shipments"
                      sortKey={{ kind: "metric", group: "totals", metric: "shipments" }}
                      sort={sort}
                      onSort={handleSort}
                    />
                  </th>
                  <th className="linehaul-outbound-planning__sticky linehaul-outbound-planning__sticky--3">
                    <SortableHeader
                      label="Pieces"
                      sortKey={{ kind: "metric", group: "totals", metric: "pieces" }}
                      sort={sort}
                      onSort={handleSort}
                    />
                  </th>
                  <th className="linehaul-outbound-planning__sticky linehaul-outbound-planning__sticky--4">
                    <SortableHeader
                      label="Weight"
                      sortKey={{ kind: "metric", group: "totals", metric: "weight" }}
                      sort={sort}
                      onSort={handleSort}
                    />
                  </th>
                  <MetricHeaderTrio group="inHouse" sort={sort} onSort={handleSort} />
                  <MetricHeaderTrio group="inbound" sort={sort} onSort={handleSort} />
                  <MetricHeaderTrio group="pickupsAggregate" sort={sort} onSort={handleSort} />
                  <MetricHeaderTrio group="pickupsUnassigned" sort={sort} onSort={handleSort} />
                  <MetricHeaderTrio group="pickupsAssigned" sort={sort} onSort={handleSort} />
                  <MetricHeaderTrio group="pickupsCompleted" sort={sort} onSort={handleSort} />
                </tr>
              </thead>
              {sections.map((section) => {
                const sectionTotals = computeHubSectionTotals(section.rows, freightIncludes);
                const labelText = section.hubName + (section.rows.length > 0 ? ` · ${section.rows.length} terminal${section.rows.length === 1 ? "" : "s"}` : "");
                return (
                  <tbody key={section.hubId ?? "other"}>
                    {showSectionHeaders && (
                      <tr className="linehaul-outbound-planning__section-label-row">
                        <td colSpan={GRID_COLUMN_COUNT}>
                          <span className="linehaul-outbound-planning__section-label-text">{labelText}</span>
                        </td>
                      </tr>
                    )}
                    {showSectionHeaders && (
                      <tr className="linehaul-outbound-planning__section-totals-row">
                        <td className="linehaul-outbound-planning__sticky linehaul-outbound-planning__sticky--1">
                          <span className="linehaul-outbound-planning__hub-totals-label">Hub totals</span>
                        </td>
                        <td className="linehaul-outbound-planning__sticky linehaul-outbound-planning__sticky--2">
                          {sectionTotals.totals.shipments.toLocaleString()}
                        </td>
                        <td className="linehaul-outbound-planning__sticky linehaul-outbound-planning__sticky--3">
                          {sectionTotals.totals.pieces.toLocaleString()}
                        </td>
                        <td className="linehaul-outbound-planning__sticky linehaul-outbound-planning__sticky--4">
                          {sectionTotals.totals.weight.toLocaleString()}
                        </td>
                        <MetricCells m={sectionTotals.inHouse} dimmed={!freightIncludes.inHouse} />
                        <MetricCells m={sectionTotals.inbound} dimmed={!freightIncludes.inbound} />
                        <MetricCells m={sectionTotals.pickupsTotal} dimmed={pickupsAggregate.noneChecked} />
                        <MetricCells m={sectionTotals.pickupsUnassigned} dimmed={!freightIncludes.pickupsUnassigned} />
                        <MetricCells m={sectionTotals.pickupsAssigned} dimmed={!freightIncludes.pickupsAssigned} />
                        <MetricCells m={sectionTotals.pickupsCompleted} dimmed={!freightIncludes.pickupsCompleted} />
                      </tr>
                    )}
                    {section.rows.map((row) => (
                      <PlanningRow
                        key={row.destinationTerminalNumber}
                        row={row}
                        adjustedTotals={computeAdjustedTotals(row, freightIncludes)}
                        included={freightIncludes}
                      />
                    ))}
                  </tbody>
                );
              })}
            </table>
          </div>
        </>
      )}
    </ContentContainer>
  );
};
